import React, { useState, useEffect } from 'react';
import { Tabs, Button, Tag, Descriptions, Table, Spin, Empty } from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { educationSessionService } from '@iso/services';
import SessionDetailModal from './SessionDetailModal';

const { TabPane } = Tabs;

const getStatusTag = (status) => {
  const map = {
    compliant: { className: 'status-compliant', label: 'Competent' },
    expiring_soon: { className: 'status-expiring', label: 'Expiring Soon' },
    expired: { className: 'status-expired', label: 'Expired' },
    not_competent: { className: 'status-missing', label: 'Not Competent' },
  };
  const s = map[status] || { className: 'status-missing', label: status || '—' };
  return <Tag className={s.className}>{s.label}</Tag>;
};

const competencyTag = (status) =>
  status === 'competent'
    ? <Tag color="success">Competent</Tag>
    : <Tag color="warning">Not Competent</Tag>;

const TrainingAssetDetail = ({ asset, propertyId, onBack, onRunSession }) => {
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  // Which session's detail is open. SessionDetailModal fetches it, so this
  // screen only has to name the row that was clicked.
  const [detailSessionId, setDetailSessionId] = useState(null);

  useEffect(() => {
    if (asset) fetchSessions();
  }, [asset]);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await educationSessionService.getByEducationAsset(asset.id);
      if (res) setSessions(res.education_sessions || []);
    } finally {
      setLoadingSessions(false);
    }
  };

  const sessionColumns = [
    {
      title: 'Date',
      dataIndex: 'session_date',
      key: 'session_date',
      render: d => moment(d).format('DD/MM/YYYY'),
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer_name',
      key: 'trainer_name',
      render: t => t || '—',
    },
    {
      title: 'Location',
      dataIndex: 'location_name',
      key: 'location_name',
      render: l => l || '—',
    },
    {
      title: 'Delivery Method',
      dataIndex: 'delivery_method',
      key: 'delivery_method',
      render: m => m ? <Tag>{m.replace(/_/g, ' ')}</Tag> : '—',
    },
    {
      title: 'Attendees',
      key: 'attendees',
      render: (_, r) => (r.trainees || []).length,
    },
  ];

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ borderRadius: 8 }}
        >
          Back to Assets
        </Button>
        <h3 style={{ margin: 0, color: '#0B2A1B', fontWeight: 700 }}>{asset.name}</h3>
        <div style={{ flex: 1 }} />
        {onRunSession && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            style={{ background: '#0B2A1B', borderColor: '#0B2A1B', borderRadius: 8 }}
            onClick={onRunSession}
          >
            Run Training Session
          </Button>
        )}
      </div>

      <Tabs defaultActiveKey="details">
        <TabPane tab="Details" key="details">
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
            <Descriptions title="Asset Information" bordered column={2}>
              <Descriptions.Item label="Name">{asset.name}</Descriptions.Item>
              <Descriptions.Item label="Category">
                {asset.category ? <Tag>{asset.category}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Education Type">
                {asset.type ? <Tag color="blue">{asset.type}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {asset.status ? <Tag color={asset.status === 'active' ? 'success' : 'default'}>{asset.status}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Primary Trainer">
                {asset.primary_trainer_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Validity Period">
                {asset.validity_months ? `${asset.validity_months} months` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Available to Contractors">
                {asset.available_to_contractors ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Total Sessions">
                {sessions.length}
              </Descriptions.Item>
            </Descriptions>
            {asset.description && (
              <div style={{ marginTop: 20 }}>
                <strong>Description</strong>
                <p style={{ marginTop: 8, color: '#595959' }}>{asset.description}</p>
              </div>
            )}
          </div>
        </TabPane>

        <TabPane tab={`Sessions (${sessions.length})`} key="sessions">
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
            {loadingSessions ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : sessions.length === 0 ? (
              <Empty description="No sessions yet" />
            ) : (
              <Table
                dataSource={sessions}
                columns={sessionColumns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 700 }}
                onRow={(record) => ({
                  onClick: () => setDetailSessionId(record.id),
                  style: { cursor: 'pointer' },
                })}
              />
            )}
          </div>
        </TabPane>

        <TabPane tab="Linked People" key="linked">
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
            {loadingSessions ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <Table
                dataSource={sessions.flatMap(s =>
                  (s.trainees || []).map(t => ({
                    ...t,
                    session_date: s.session_date,
                    trainer_name: s.trainer_name,
                  }))
                )}
                columns={[
                  {
                    title: 'Person Name',
                    key: 'name',
                    render: (_, r) => <span style={{ fontWeight: 600 }}>{r.name}</span>,
                  },
                  {
                    title: 'Type',
                    dataIndex: 'trainee_type',
                    key: 'trainee_type',
                    render: t => <Tag>{t === 'property_user' ? 'Staff' : 'External'}</Tag>,
                  },
                  {
                    title: 'Completed Date',
                    dataIndex: 'session_date',
                    key: 'completed_date',
                    render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
                  },
                  {
                    title: 'Competency',
                    dataIndex: 'competency_status',
                    key: 'competency_status',
                    render: competencyTag,
                  },
                ]}
                rowKey={(r, i) => `${r.user_id || r.email}_${i}`}
                pagination={{ pageSize: 10 }}
              />
            )}
          </div>
        </TabPane>
      </Tabs>


      <SessionDetailModal
        visible={detailSessionId !== null}
        sessionId={detailSessionId}
        propertyId={propertyId}
        onClose={() => setDetailSessionId(null)}
        // Competency changes show up in the Linked People tab and the session
        // attendee counts, so re-read the asset's sessions when one lands.
        onChanged={fetchSessions}
      />
    </div>
  );
};

export default TrainingAssetDetail;
